import type { HTMLAttributes, ReactNode } from "react";
import { createContext, useContext } from "react";
import {
  GridList as ReactAriaGridList,
  GridListItem as ReactAriaGridListItem,
  type GridListItemProps as ReactAriaGridListItemProps,
  type GridListProps as ReactAriaGridListProps,
} from "react-aria-components";

import { useI18n } from "../use-i18n";
import { cn, cv } from "../utils";
import { NcFormattedMessage } from "./nc-formatted-message";
import { NcLoadingOverlay } from "./nc-loading-overlay";

export type GridListTableBreakpoint = "sm" | "md" | "lg" | "xl" | "2xl";

const defaultBreakpoint = "lg";

const GridListTableContext = createContext<{
  tableLayoutBreakpoint: GridListTableBreakpoint;
  emptyMessage?: string;
}>({ tableLayoutBreakpoint: defaultBreakpoint });

const FilterSortContext = createContext<{
  gridListId: string | null;
}>({ gridListId: null });

// Tailwind variants for different breakpoints
const gridListTableVariants = cv("grid w-full", {
  variants: {
    breakpoint: {
      lg: "lg:table",
      md: "md:table",
      sm: "sm:table",
      xl: "xl:table",
      "2xl": "2xl:table",
    },
  },
  defaultVariants: {
    breakpoint: "lg",
  },
});

const headerVariants = cv("hidden", {
  variants: {
    breakpoint: {
      lg: "lg:table-row",
      md: "md:table-row",
      sm: "sm:table-row",
      xl: "xl:table-row",
      "2xl": "2xl:table-row",
    },
  },
  defaultVariants: {
    breakpoint: "lg",
  },
});

const headerCellVariants = cv("font-bold text-main", {
  variants: {
    breakpoint: {
      lg: "lg:table-cell lg:border-b lg:border-ui lg:p-2 lg:px-3",
      md: "md:table-cell md:border-b md:border-ui md:p-2 md:px-3",
      sm: "sm:table-cell sm:border-b sm:border-ui sm:p-2 sm:px-3",
      xl: "xl:table-cell xl:border-b xl:border-ui xl:p-2 xl:px-3",
      "2xl": "2xl:table-cell 2xl:border-b 2xl:border-ui 2xl:p-2 2xl:px-3",
    },
  },
  defaultVariants: {
    breakpoint: "lg",
  },
});

const bodyRowVariants = cv(
  "grid gap-2 transition-colors duration-150 p-2 px-3 data-focus-visible:outline-active data-focus-visible:outline-2 data-focus-visible:outline-offset-2 border-b border-ui last:border-b-0 sm:grid-cols-2",
  {
    variants: {
      breakpoint: {
        lg: "lg:table-row",
        md: "md:table-row",
        sm: "sm:table-row",
        xl: "xl:table-row",
        "2xl": "2xl:table-row",
      },
    },
    defaultVariants: {
      breakpoint: "lg",
    },
  }
);

const bodyCellVariants = cv("p-2 px-3", {
  variants: {
    breakpoint: {
      lg: "lg:table-cell lg:align-middle",
      md: "md:table-cell md:align-middle",
      sm: "sm:table-cell sm:align-middle",
      xl: "xl:table-cell xl:align-middle",
      "2xl": "2xl:table-cell 2xl:align-middle",
    },
  },
  defaultVariants: {
    breakpoint: "lg",
  },
});

const bodyCellHeadingVariants = cv("font-bold text-main mb-1", {
  variants: {
    breakpoint: {
      lg: "lg:sr-only",
      md: "md:sr-only",
      sm: "sm:sr-only",
      xl: "xl:sr-only",
      "2xl": "2xl:sr-only",
    },
  },
  defaultVariants: {
    breakpoint: "lg",
  },
});

interface GridListTableStateProps {
  error?: boolean | string;
  emptyMessage?: string;
  isLoading?: boolean;
}

interface GridListTableProps extends HTMLAttributes<HTMLDivElement>, GridListTableStateProps {
  children: ReactNode;
  tableLayoutBreakpoint?: GridListTableBreakpoint;
}

export const NcGridListTable = ({
  children,
  tableLayoutBreakpoint = defaultBreakpoint,
  emptyMessage,
  isLoading,
  error,
  className,
  ...props
}: GridListTableProps) => {
  if (error) {
    return <GridListTableError error={error} />;
  }
  return (
    <GridListTableContext.Provider value={{ tableLayoutBreakpoint, emptyMessage }}>
      <NcLoadingOverlay isLoading={isLoading}>
        <div
          data-nc="NcGridListTable"
          className={cn(gridListTableVariants({ breakpoint: tableLayoutBreakpoint }), className)}
          {...props}
        >
          {children}
        </div>
      </NcLoadingOverlay>
    </GridListTableContext.Provider>
  );
};

const GridListTableError = ({ error }: { error: GridListTableProps["error"] }) => {
  const { t } = useI18n();
  const errorText = typeof error === "string" ? error : t("error_default");
  return <NcFormattedMessage variant="danger">{errorText}</NcFormattedMessage>;
};

type HeaderProps = HTMLAttributes<HTMLDivElement>;

const Header = ({ children, className, ...props }: HeaderProps) => {
  const { tableLayoutBreakpoint } = useContext(GridListTableContext);
  return (
    <div
      data-nc="NcGridListTable.Header"
      role="presentation"
      className={cn(headerVariants({ breakpoint: tableLayoutBreakpoint }), className)}
      {...props}
    >
      {children}
    </div>
  );
};

type HeaderCellProps = HTMLAttributes<HTMLDivElement>;

const HeaderCell = ({ children, className, ...props }: HeaderCellProps) => {
  const { tableLayoutBreakpoint } = useContext(GridListTableContext);
  return (
    <div
      data-nc="NcGridListTable.HeaderCell"
      className={cn(headerCellVariants({ breakpoint: tableLayoutBreakpoint }), className)}
      {...props}
    >
      {children}
    </div>
  );
};

const Body = function <T extends object>({ ...props }: ReactAriaGridListProps<T>) {
  const { t } = useI18n();
  const { gridListId } = useContext(FilterSortContext);
  const { emptyMessage } = useContext(GridListTableContext);
  const emptyText = typeof emptyMessage === "string" ? emptyMessage : t("empty_default");
  const contents = (
    <ReactAriaGridList
      className="table-row-group"
      {...{
        ...(gridListId ? { id: gridListId } : {}),
        renderEmptyState: () => (
          <NcFormattedMessage variant="secondary" className="p-3">
            {emptyText}
          </NcFormattedMessage>
        ),
        ...props,
      }}
    />
  );

  return gridListId ? (
    <div className="contents" aria-live="polite">
      {contents}
    </div>
  ) : (
    <>{contents}</>
  );
};

export type NcGridListTableBodyRowProps = ReactAriaGridListItemProps;

const BodyRow = ({ children, className, ...props }: ReactAriaGridListItemProps) => {
  const contextBreakpoint = useContext(GridListTableContext);
  return (
    <ReactAriaGridListItem
      data-nc="NcGridListTable.BodyRow"
      className={cn(
        bodyRowVariants({
          breakpoint: contextBreakpoint.tableLayoutBreakpoint,
        }),
        // Interactive states
        "data-react-aria-pressable:cursor-pointer data-selection-mode:cursor-pointer",
        "data-hovered:bg-focused data-focused:bg-focused",
        "data-focus-visible:outline-auto data-focus-visible:outline-active data-focus-visible:outline data-focus-visible:outline-offset-1",
        className
      )}
      {...props}
    >
      {children}
    </ReactAriaGridListItem>
  );
};

interface BodyCellProps extends HTMLAttributes<HTMLDivElement> {
  children?: ReactNode;
  heading: string;
}

const BodyCell = ({ children, heading, className, ...props }: BodyCellProps) => {
  const contextBreakpoint = useContext(GridListTableContext);
  return (
    <div
      data-nc="NcGridListTable.BodyCell"
      className={cn(
        bodyCellVariants({
          breakpoint: contextBreakpoint.tableLayoutBreakpoint,
        }),
        className
      )}
      {...props}
    >
      <div
        className={cn(
          bodyCellHeadingVariants({
            breakpoint: contextBreakpoint.tableLayoutBreakpoint,
          })
        )}
      >
        {heading}
      </div>
      {children}
    </div>
  );
};

NcGridListTable.Header = Header;
NcGridListTable.HeaderCell = HeaderCell;
NcGridListTable.Body = Body;
NcGridListTable.BodyRow = BodyRow;
NcGridListTable.BodyCell = BodyCell;
